import { useMemo, useState, useEffect } from 'react';
import Selector from '../../components/Selector/Selector';
import SystemRegisteredContractTable from '../../components/OpmContractList/SystemRegisteredContractTable';
import { useDRResources, useDRResource } from '../../hooks/queries/drResourceQueries';
import {
  useOpmContractNumberInfoSummary,
  // useOpmDataLinkageComparison,
  useCpoDataProblem,
} from '../../hooks/queries/resourceRelationQueries';
import { getOpmContractNumber } from '../../components/OpmContractList/OpmContractListHelpers.jsx';
import '../PowerMeterContractDetail/PowerMeterContractDetail.css';
import './OpmDataLinkageStatus.css';

function contractSetFromRows(rows) {
  const set = new Set();
  for (const row of rows || []) {
    const n = String(getOpmContractNumber(row)).trim();
    if (n) set.add(n);
  }
  return set;
}

function RatioBarRow({ label, numerator, denominator, barClass }) {
  const d = Math.max(1, denominator || 0);
  const n = Math.min(numerator ?? 0, d);
  const pct = Math.round((n / d) * 1000) / 10;
  const widthPct = Math.min(100, (n / d) * 100);
  return (
    <div className="opm-linkage-ratio-row">
      <div className="opm-linkage-ratio-row__head">
        <span className="text-b2-400 text-gray-600">{label}</span>
        <span className="text-b2-600 text-primary">{pct}%</span>
      </div>
      <div className="opm-linkage-ratio-row__track">
        <div
          className={`opm-linkage-ratio-row__fill ${barClass}`}
          style={{ width: `${widthPct}%` }}
        />
      </div>
      <p className="opm-linkage-ratio-row__fraction text-b2-400 text-gray-600">
        {n}/{d}
      </p>
    </div>
  );
}

function DiffTable({ title, rows, footnote, actions }) {
  const emptyMessage = footnote
    ? '비교 목록을 사용할 수 없습니다.'
    : rows.length === 0
      ? '차이 항목이 없습니다.'
      : null;
  return (
    <div className="opm-linkage-column-inner flex flex-col gap-8">
      <div className="flex align-center justify-between flex-wrap gap-5">
        <div className="flex align-center gap-5 flex-wrap">
          <h3 className="text-b1-700 text-base">{title}</h3>
          <p className="text-b2-400 text-gray-600">({rows.length})</p>
        </div>
        {actions && <div className="opm-diff-table-actions">{actions}</div>}
      </div>
      {footnote ? (
        <p className="text-b2-400 text-warning">{footnote}</p>
      ) : rows.length === 0 ? (
        <p className="text-b2-400 text-gray-600">{emptyMessage}</p>
      ) : (
        <div className="primary-table">
          <table>
            <thead>
              <tr>
                <th>계약번호</th>
                <th>비고</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={`${r.contractNumber}-${r.remark}`}>
                  <td>{r.contractNumber}</td>
                  <td>{r.remark}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

const OpmDataLinkageStatus = () => {
  const [selectedDRResourceId, setSelectedDRResourceId] = useState(null);
  const [selectedDRResource, setSelectedDRResource] = useState(null);
  const [cpoDays, setCpoDays] = useState(7);

  const { data: drResourcesData, isLoading: drListLoading } = useDRResources({
    Page: 1,
    PageSize: 1000,
  });

  const drResourceOptions = useMemo(() => {
    if (!drResourcesData?.Items) return [];
    return drResourcesData.Items.map((resource) => ({
      id: resource.DrResourceSysId,
      label: resource.ResourceName || resource.DrResourceSysId,
      data: resource,
    }));
  }, [drResourcesData]);

  useEffect(() => {
    if (drResourceOptions.length > 0 && !selectedDRResourceId && !drListLoading) {
      const first = drResourceOptions[0];
      setSelectedDRResourceId(first.id);
      setSelectedDRResource(first.data);
    }
  }, [drResourceOptions, selectedDRResourceId, drListLoading]);

  const handleDRResourceChange = (item) => {
    if (item) {
      setSelectedDRResourceId(item.id);
      setSelectedDRResource(item.data);
    } else {
      setSelectedDRResourceId(null);
      setSelectedDRResource(null);
    }
  };

  const {
    data: drResource,
    isLoading: drLoading,
    isError: drIsError,
    error: drError,
  } = useDRResource(selectedDRResourceId, { enabled: !!selectedDRResourceId });

  const { data: summaryRaw, isLoading: summaryLoading } = useOpmContractNumberInfoSummary(
    { DrResourceSysId: selectedDRResourceId },
    { enabled: !!selectedDRResourceId }
  );

  const opmList = useMemo(() => {
    const raw = drResource?.OpmContractInfos;
    return Array.isArray(raw) ? raw : [];
  }, [drResource]);

  const systemSet = useMemo(() => contractSetFromRows(opmList), [opmList]);

  const summary = useMemo(() => {
    if (summaryRaw == null || typeof summaryRaw !== 'object') return null;
    return {
      totalCount: Number(summaryRaw.TotalCount ?? summaryRaw.totalCount ?? 0),
      connectedCount: Number(summaryRaw.ConnectedCount ?? summaryRaw.connectedCount ?? 0),
    };
  }, [summaryRaw]);


  const opmRegisteredTotal = useMemo(() => {
    if (summary?.totalCount != null && summary.totalCount > 0) return summary.totalCount;
    return Math.max(
      systemSet.size,
      1
    );
  }, [summary, systemSet]);

  const connectedInSystem = useMemo(() => {
    if (summary?.connectedCount != null && summary.totalCount != null) {
      return summary.connectedCount;
    }
    return opmList.filter((r) => r?.ConnectStatus === true).length;
  }, [summary, opmList]);

  const {
    data: cpoDataProblemRaw,
    isLoading: cpoLoading,
    isError: cpoIsError,
    error: cpoError,
    totalCount: cpoTotalCount,
  } = useCpoDataProblem({ DrResourceSysId: selectedDRResourceId, Days: cpoDays });
  
  const cpoDataProblemRows = useMemo(() => {
    if (!cpoDataProblemRaw || typeof cpoDataProblemRaw !== 'object') return [];
    return Object.entries(cpoDataProblemRaw).map(([contractNumber, remark]) => ({
      contractNumber,
      remark,
    }));
  }, [cpoDataProblemRaw]);

  const cpoFootnote = cpoIsError
    ? `CPO 데이터 문제 목록을 불러오지 못했습니다. (${cpoError?.message || 'API 확인 필요'})`
    : null;

  const systemCount = systemSet.size;
  const denom = opmRegisteredTotal;

  return (
    <div className="page-container opm-data-linkage-page">
      <div className="opm-data-linkage-page__inner">
        {/* <h1 className="text-h2 text-primary mb-5">고객계약번호 상태 조회</h1> */}

        <div className="opm-data-linkage-selector mb-10">
          <label className="opm-data-linkage-selector__label text-b2-700 text-gray-700" htmlFor="opm-linkage-dr-select">
            DR 선택
          </label>
          <div id="opm-linkage-dr-select" className="opm-data-linkage-selector__control">
            <Selector
              mode="local"
              options={drResourceOptions}
              value={
                selectedDRResource
                  ? {
                      id: selectedDRResource.DrResourceSysId,
                      label: selectedDRResource.ResourceName || selectedDRResource.DrResourceSysId,
                      data: selectedDRResource,
                    }
                  : null
              }
              onChange={handleDRResourceChange}
              placeholder={drListLoading ? '목록 불러오는 중...' : 'DR 자원을 선택하세요'}
              disabled={drListLoading}
              enableSearch
              emptyMessage="DR 자원이 없습니다"
              className="w-full"
            />
          </div>
        </div>

        {!selectedDRResourceId ? (
          <p className="text-b2-400 text-gray-600">DR 자원을 선택하면 연동 현황이 표시됩니다.</p>
        ) : drLoading ? (
          <div className="detail-loading opm-data-linkage-loading">
            <div className="loading-spinner" />
            <p className="text-b1-400 text-base">DR 자원 정보를 불러오는 중...</p>
          </div>
        ) : drIsError ? (
          <div className="detail-error">
            <p className="text-b1-400 text-base">{drError?.message || 'DR 정보를 불러오지 못했습니다.'}</p>
          </div>
        ) : (
          <>
            <section className="opm-linkage-summary mb-10">

              <div className="opm-linkage-summary__grid">
                <div className="opm-linkage-summary-card">
                  <p className="opm-linkage-summary-card__label text-b2-400 text-gray-600">
                    DR에 할당된 OPM 고객계약번호 수
                  </p>
                  <p className="opm-linkage-summary-card__value text-h2 text-primary">
                    {summaryLoading && !summary ? '…' : denom}
                  </p>
                </div>
                <div className="opm-linkage-summary-card opm-linkage-summary-card--ratio">
                  <RatioBarRow
                    label="OPM API 연결 비율"
                    numerator={connectedInSystem}
                    denominator={Math.max(1, systemCount)}
                    barClass="opm-linkage-ratio-row__fill--dr"
                  />
                </div>
                <div className="opm-linkage-summary-card opm-linkage-summary-card--ratio">
                  <RatioBarRow
                    label="제주 전기차 API 제공 비율"
                    numerator={denom - (cpoDataProblemRows?.length ?? 0) || 0}
                    denominator={denom}
                    barClass="opm-linkage-ratio-row__fill--jeju"
                  />
                </div>
              </div>
            </section>

            <div className="opm-data-linkage-tri-grid">
              <div className="opm-data-linkage-column">
                <SystemRegisteredContractTable
                  opmList={opmList}
                  listTitle={`DR에 할당된 OPM 고객계약번호 목록 및 상태`}
                />
              </div>
              <div className="opm-data-linkage-column">
                {cpoLoading ? (
                  <p className="text-b2-400 text-gray-600">CPO 데이터 문제 목록 불러오는 중...</p>
                ) : (
                  <DiffTable
                    title="CPO의 데이터 미제공 고객계약번호 목록"
                    rows={cpoDataProblemRows}
                    footnote={cpoFootnote}
                    actions={
                      <>
                        {[7, 30].map((d) => (
                          <button
                            key={d}
                            className={`opm-days-btn${cpoDays === d ? ' opm-days-btn--active' : ''}`}
                            onClick={() => setCpoDays(d)}
                          >
                            {d}일전
                          </button>
                        ))}
                      </>
                    }
                  />
                )}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default OpmDataLinkageStatus;
